import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { wsUrl } from '../api';

// Backoff config
const MAX_BACKOFF_EXPONENT = 6;
const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 30000;
const BACKOFF_JITTER_MS = 300;

/**
 * Owns a single shared WebSocket connection for the authenticated user with
 * exponential backoff + jitter. Subscribers register handlers via the
 * returned `subscribe` function; we also surface in-app toast fallbacks when
 * the tab is hidden so users don't miss events without push permission.
 */
export const useSharedWebSocket = (userId) => {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const subscribersRef = useRef(new Set());
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);

  const closeSocket = useCallback(() => {
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (err) {
        console.debug('WS close noop:', err?.message);
      }
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!userId) {
      closeSocket();
      setConnected(false);
      return undefined;
    }
    let cancelled = false;

    const handleMessage = (ev) => {
      let data;
      try {
        data = JSON.parse(ev.data);
      } catch (err) {
        console.warn('WS message parse error:', err?.message);
        return;
      }
      // In-app toast fallback when the tab is hidden so users don't miss
      // events even when Web Push hasn't been granted.
      if (document.visibilityState !== 'visible') {
        if (data?.type === 'message') {
          const m = data.message;
          toast('New message', { description: m?.content || 'Sent you a photo' });
        } else if (data?.type === 'profile_view' && data.viewer) {
          toast('Someone viewed your profile', {
            description: `${data.viewer.name} just checked out your profile`,
          });
        }
      }
      subscribersRef.current.forEach((fn) => {
        try { fn(data); } catch (err) { console.error('WS subscriber error:', err); }
      });
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      const attempt = Math.min(reconnectAttemptsRef.current, MAX_BACKOFF_EXPONENT);
      const base = Math.min(BASE_BACKOFF_MS * Math.pow(2, attempt), MAX_BACKOFF_MS);
      const jitter = Math.random() * BACKOFF_JITTER_MS;
      reconnectTimerRef.current = setTimeout(connect, base + jitter);
      reconnectAttemptsRef.current = attempt + 1;
    };

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      ws.onopen = () => {
        if (cancelled) return;
        reconnectAttemptsRef.current = 0;
        setConnected(true);
      };
      ws.onclose = () => {
        if (cancelled) return;
        setConnected(false);
        scheduleReconnect();
      };
      ws.onerror = () => { /* close fires next; backoff handles it */ };
      ws.onmessage = handleMessage;
    };
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectAttemptsRef.current = 0;
      closeSocket();
    };
  }, [userId, closeSocket]);

  const subscribe = useCallback((handler) => {
    subscribersRef.current.add(handler);
    return () => subscribersRef.current.delete(handler);
  }, []);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      try {
        ws.send(JSON.stringify(payload));
        return true;
      } catch (err) {
        console.warn('WS send failed:', err?.message);
      }
    }
    return false;
  }, []);

  return { connected, subscribe, send, close: closeSocket };
};
